"use client"

import { useState, useEffect, useRef, useCallback, useMemo } from "react"

interface Message {
  id: number
  text: string
  sender: "ai" | "user"
  delay: number
  isPlaceholder?: boolean
}

interface ConversationData {
  name: string
  backgroundImage: string
  messages: Array<{
    sender: "ai" | "user"
    text: string
    isPlaceholder?: boolean
  }>
}

interface AnimatedChatbotDemoProps {
  className?: string
  conversations: ConversationData[]
  headerTitle: string
  inputPlaceholder: string
  brandName: string
  todayLabel: string
  theme?: "light" | "dark"
  largeText?: boolean
  direction?: "ltr" | "rtl"
}

// Generate delays for messages
function generateDelays(messageCount: number): number[] {
  const delays: number[] = []
  let currentDelay = 0
  for (let i = 0; i < messageCount; i++) {
    if (i === 0) {
      currentDelay = 0
    } else if (i === 1) {
      currentDelay = 4500
    } else {
      // Alternate between shorter and longer delays for natural feel
      currentDelay += (i % 2 === 0) ? 10000 : 6000
    }
    delays.push(currentDelay)
  }
  // Add placeholder delay at the end
  delays.push(delays[delays.length - 1] + 5000)
  return delays
}

function formatTimestamp(date: Date, todayLabel: string): string {
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${todayLabel} ${hours}:${minutes}`
}

export default function AnimatedChatbotDemo({ 
  className = "",
  conversations,
  headerTitle,
  inputPlaceholder,
  brandName,
  todayLabel,
  theme = "light",
  largeText = false,
  direction = "rtl",
}: AnimatedChatbotDemoProps) {
  const isRtl = direction === "rtl"
  const isDark = theme === "dark"
  const fontSizes = {
    header: largeText ? '16px' : '14px',
    message: largeText ? '14px' : '12px',
    timestamp: largeText ? '12px' : '11px',
    input: largeText ? '16px' : '14px',
    brand: largeText ? '12px' : '10px',
    emojiFallback: largeText ? '26px' : '24px',
  }
  const colors = {
    widgetBg: isDark ? "#353535" : "#f8f9fa",
    widgetBorder: isDark ? "rgba(255,255,255,0.06)" : "#e9ecef",
    headerBg: isDark ? "#0970F3" : "#0d7ad9",
    messagesBg: isDark ? "#353535" : "#f8f9fa",
    inputBg: isDark ? "#3d3d3d" : "#ffffff",
    inputBorder: isDark ? "rgba(255,255,255,0.06)" : "#e9ecef",
    userBubbleBg: isDark ? "#3d3d3d" : "white",
    userBubbleColor: isDark ? "#f5f6f6" : "#333",
    aiBubbleBg: isDark ? "#0970F3" : "#0d7ad9",
    timestampColor: isDark ? "#8b8f98" : "#999",
    inputColor: isDark ? "#f5f6f6" : "#333",
    iconColor: isDark ? "#0970F3" : "#0d7ad9",
    scrollbarTrack: isDark ? "#353535" : "#f8f9fa",
    scrollbarThumb: isDark ? "#3a3a3f" : "#cbd5e0",
    scrollbarThumbHover: isDark ? "#4a4a4f" : "#a0aec0",
  }
  const [currentConversationIndex, setCurrentConversationIndex] = useState(0)
  const [visibleMessages, setVisibleMessages] = useState<number[]>([])
  const [loopKey, setLoopKey] = useState(0)
  
  const currentConversation = conversations[currentConversationIndex] ?? conversations[0]
  
  // Reset animation when conversations change (e.g. locale switch)
  useEffect(() => {
    setCurrentConversationIndex(0)
    setVisibleMessages([])
    setLoopKey((prev) => prev + 1)
  }, [conversations])
  
  // Memoize delays - only recalculate when conversation message count changes
  const delays = useMemo(() => {
    return generateDelays(currentConversation.messages.length)
  }, [currentConversation.messages.length])
  
  // Memoize messages - only recalculate when conversation changes
  // This is critical: without memoization, the messages array is recreated on every render,
  // causing the useEffect to re-run and clear all timeouts before they can fire
  const messages = useMemo<Message[]>(() => {
    return currentConversation.messages.map((msg, idx) => ({
      id: idx + 1,
      text: msg.text,
      sender: msg.sender,
      delay: delays[idx],
      isPlaceholder: msg.isPlaceholder
    }))
  }, [currentConversation, delays])

  // Refs for scrolling control
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const messageRefs = useRef<Record<number, HTMLDivElement | null>>({})

  // Enhanced scrolling functions with animation awareness
  const scrollToLastMessage = useCallback(() => {
    if (visibleMessages.length === 0 || !messagesContainerRef.current) return
    
    const lastMessageId = visibleMessages[visibleMessages.length - 1]
    const lastMessageElement = messageRefs.current[lastMessageId]
    
    if (lastMessageElement && messagesContainerRef.current) {
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current
        const message = lastMessageElement
        
        if (!container || !message) return
        
        const containerHeight = container.clientHeight
        const messageTop = message.offsetTop
        const messageHeight = message.offsetHeight
        const containerScrollTop = container.scrollTop
        
        const newScrollTop = messageTop - containerHeight + messageHeight
        
        container.scrollTo({
          top: newScrollTop,
          behavior: 'smooth'
        })
      })
    }
  }, [visibleMessages])

  const scrollToTop = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }
  }, [])

  useEffect(() => {
    // Add custom thin scrollbar styles
    const style = document.createElement('style')
    style.id = 'chatbot-scrollbar-styles'
    style.textContent = `
      #chatbot-messages-container::-webkit-scrollbar {
        width: 6px;
      }
      #chatbot-messages-container::-webkit-scrollbar-track {
        background: ${colors.scrollbarTrack};
      }
      #chatbot-messages-container::-webkit-scrollbar-thumb {
        background: ${colors.scrollbarThumb};
        border-radius: 3px;
      }
      #chatbot-messages-container::-webkit-scrollbar-thumb:hover {
        background: ${colors.scrollbarThumbHover};
      }
    `
    if (!document.getElementById('chatbot-scrollbar-styles')) {
      document.head.appendChild(style)
    }
    
    // Reset messages and scroll to top for new conversation
    setVisibleMessages([])
    scrollToTop()
    
    const timeouts: NodeJS.Timeout[] = []
    
    // Show messages sequentially
    messages.forEach((msg) => {
      const timeout = setTimeout(() => {
        setVisibleMessages((prev) => [...prev, msg.id])
      }, msg.delay)
      timeouts.push(timeout)
    })

    // After conversation finishes, switch to next conversation
    const totalDuration = messages[messages.length - 1].delay + 3000
    const switchTimeout = setTimeout(() => {
      setCurrentConversationIndex((prev) => (prev + 1) % conversations.length)
      setLoopKey((prev) => prev + 1)
    }, totalDuration)

    return () => {
      timeouts.forEach(clearTimeout)
      clearTimeout(switchTimeout)
    }
  }, [loopKey, currentConversationIndex, scrollToTop, conversations.length, messages, colors.scrollbarTrack, colors.scrollbarThumb, colors.scrollbarThumbHover])

  // Auto-scroll to last message when new messages appear
  useEffect(() => {
    if (visibleMessages.length > 0) {
      scrollToLastMessage()
    }
  }, [visibleMessages, scrollToLastMessage])

  return (
    <div 
      className={`relative w-full ${className}`}
      style={{
        minHeight: '700px',
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background image container */}
      {currentConversation.backgroundImage && (
        <div
          className="absolute transition-all duration-1000"
          style={{
            backgroundImage: `url(${currentConversation.backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            width: '100%',
            height: '480px',
            top: '50%',
            left: '0',
            transform: 'translateY(-50%)',
            zIndex: 0,
            boxShadow: `
              8px 8px 25px rgba(0, 0, 0, 0.15),
              4px 4px 12px rgba(0, 0, 0, 0.12),
              2px 2px 6px rgba(0, 0, 0, 0.1),
              inset 0 1px 3px rgba(0, 0, 0, 0.08)
            `,
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            filter: 'brightness(1.01) contrast(1.02)',
            position: 'relative',
          }}
        >
          {/* Subtle gradient overlay */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to right, rgba(0, 0, 0, 0.01) 0%, transparent 50%, rgba(0, 0, 0, 0.02) 100%)',
              borderRadius: '12px',
              pointerEvents: 'none',
            }}
          />
          {/* Subtle right side highlight */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '1px',
              height: '100%',
              background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.15), transparent)',
              borderRadius: '0 12px 12px 0',
              pointerEvents: 'none',
            }}
          />
        </div>
      )}
      
      {/* Chat widget container */}
      <div 
        className="absolute rounded-2xl overflow-hidden shadow-2xl"
        style={{
          width: '280px',
          height: '600px',
          background: colors.widgetBg,
          borderRadius: '16px',
          border: `1px solid ${colors.widgetBorder}`,
          direction,
          fontFamily: isRtl
            ? "'Pelak', 'Vazir', 'Vazirmatn', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
            : "var(--font-inter), 'Segoe UI', system-ui, -apple-system, sans-serif",
          top: '50px',
          left: '0',
          zIndex: 1,
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-4"
          style={{
            background: colors.headerBg,
            padding: '13px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
          }}
        >
          <div className="flex items-center gap-3">
            {/* Robot icon */}
            <div className="relative flex-shrink-0" style={{ width: '55px', height: '55px', marginInlineEnd: '12px' }}>
              <img
                src="/dist2/robot-icon.png"
                alt={brandName}
                style={{ 
                  width: '55px', 
                  height: '55px', 
                  objectFit: 'contain' 
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  const fallback = document.createElement('div')
                  fallback.textContent = '🤖'
                  fallback.style.width = '55px'
                  fallback.style.height = '55px'
                  fallback.style.display = 'flex'
                  fallback.style.alignItems = 'center'
                  fallback.style.justifyContent = 'center'
                  fallback.style.fontSize = fontSizes.emojiFallback
                  target.parentNode?.appendChild(fallback)
                }}
              />
            </div>
            {/* Header title */}
            <div className="text-white font-semibold" style={{ fontSize: fontSizes.header }}>
              {headerTitle}
            </div>
          </div>
        </div>

        {/* Messages container with thin scrollbar */}
        <div 
          id="chatbot-messages-container"
          ref={messagesContainerRef}
          className="flex-1 p-4 space-y-3"
          style={{
            background: colors.messagesBg,
            maxHeight: '450px',
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarWidth: 'thin',
            scrollbarColor: `${colors.scrollbarThumb} ${colors.scrollbarTrack}`,
            scrollBehavior: 'smooth',
            scrollbarGutter: 'stable',
          }}
        >
          {messages.map((msg) => {
            const isVisible = visibleMessages.includes(msg.id)
            const isUser = msg.sender === "user"
            
            return (
              <div
                key={`${msg.id}-${loopKey}`}
                ref={(el) => { messageRefs.current[msg.id] = el }}
                className={`flex flex-col transition-all duration-500 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
                }`}
                style={{
                  alignItems: isUser
                    ? (isRtl ? "flex-start" : "flex-end")
                    : (isRtl ? "flex-end" : "flex-start"),
                  marginBottom: '12px',
                }}
              >
                {/* Message bubble */}
                {(!msg.isPlaceholder || msg.text) && (
                  <div
                    className="rounded-2xl px-4 py-3 shadow-sm select-text"
                    style={{
                      maxWidth: '80%',
                      borderRadius: isUser
                        ? (isRtl ? "20px 20px 20px 4px" : "20px 20px 4px 20px")
                        : (isRtl ? "20px 20px 4px 20px" : "20px 20px 20px 4px"),
                      background: isUser ? colors.userBubbleBg : colors.aiBubbleBg,
                      color: isUser ? colors.userBubbleColor : "white",
                      fontSize: fontSizes.message,
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word',
                      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {msg.text}
                  </div>
                )}
                
                {/* Timestamp */}
                {isVisible && (
                  <div
                    className="text-gray-400 mt-1"
                    style={{
                      fontSize: fontSizes.timestamp,
                      color: colors.timestampColor,
                      paddingInlineEnd: isUser ? '0' : '4px',
                      paddingInlineStart: isUser ? '4px' : '0',
                    }}
                  >
                    {formatTimestamp(new Date(), todayLabel)}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Input area */}
        <div
          className="flex flex-col gap-1"
          style={{
            padding: '12px 16px 16px',
            background: colors.inputBg,
            borderTop: `1px solid ${colors.inputBorder}`,
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px',
          }}
        >
          {/* Input controls: mic + field in pill, send as primary action */}
          <div className="flex items-center gap-2">
            <div
              className="flex flex-1 items-center min-w-0"
              style={{
                background: isDark ? 'rgba(255,255,255,0.05)' : '#eef1f4',
                borderRadius: '22px',
                border: `1px solid ${colors.inputBorder}`,
                paddingInlineStart: '4px',
                paddingInlineEnd: '12px',
                minHeight: '44px',
              }}
            >
              <button
                type="button"
                aria-label="Voice input"
                className="flex flex-shrink-0 items-center justify-center transition-all hover:opacity-70"
                style={{
                  width: '36px',
                  height: '36px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z" stroke={colors.iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19 10V12C19 15.87 15.87 19 12 19C8.13 19 5 15.87 5 12V10" stroke={colors.iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="19" x2="12" y2="23" stroke={colors.iconColor} strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="8" y1="23" x2="16" y2="23" stroke={colors.iconColor} strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>

              <input
                type="text"
                placeholder={inputPlaceholder}
                className="flex-1 min-w-0 bg-transparent border-none outline-none"
                style={{
                  fontSize: fontSizes.input,
                  color: colors.inputColor,
                  padding: '10px 8px',
                }}
                readOnly
              />
            </div>

            <button
              type="button"
              aria-label="Send message"
              className="flex flex-shrink-0 items-center justify-center transition-all hover:scale-105 hover:opacity-90"
              style={{
                width: '44px',
                height: '44px',
                background: colors.headerBg,
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                padding: 0,
                boxShadow: '0 2px 8px rgba(9, 112, 243, 0.35)',
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ transform: isRtl ? 'scaleX(-1)' : undefined }}
              >
                <path
                  d="M2 21L23 12L2 3V10L17 12L2 14V21Z"
                  fill="white"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          
          {/* Brand name text */}
          <div
            className="text-center"
            style={{
              fontSize: fontSizes.brand,
              color: colors.timestampColor,
              textAlign: 'center',
              marginTop: '4px',
            }}
          >
            {brandName}
          </div>
        </div>
      </div>
    </div>
  )
}
